import './index.scss';
import LogoColor from 'src/assets/svg/logo_color.svg?react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Form, Input, Space, Spin, Tag } from 'antd';
import { AxiosError } from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Title, Text, Paragraph } from 'src/shared/components/typography';
import { DeviceApiService, DeviceAuthorization, DeviceError, DeviceScope } from 'src/api/device-api';

const api = new DeviceApiService();

/** The router basename, and the service's own path base. Same value `index.tsx` passes to the router. */
const BASE_PATH = '/identity';

type Outcome = 'approved' | 'denied';

/**
 * Where Duende sends somebody to approve a device login — the `j2l` CLI is the only client that
 * asks for one. The code arrives as `?userCode=`; the form is for the person who typed the URL off
 * a terminal on another machine, which is the case the whole grant exists for.
 */
const DeviceView: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [userCode, setUserCode] = useState<string>(searchParams.get('userCode') ?? '');
  const [authorization, setAuthorization] = useState<DeviceAuthorization | undefined>();
  const [outcome, setOutcome] = useState<Outcome | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>();

  /**
   * The display names arrive in English, from the resource rows Duende seeds, so they go through
   * `t` like any other string. The scopes with no resource row behind them arrive unnamed instead.
   */
  const nameScope = (scope: DeviceScope): string => {
    if (scope.displayName) return t(scope.displayName);

    return scope.name === 'offline_access' ? t('Seguir accediendo aunque cierres el navegador') : scope.name;
  };

  const describe = useCallback(
    (e: unknown): string => {
      const error = e as AxiosError<{ code?: string }>;

      // 401 is answered by leaving: approval is bound to the cookie session, so there is nothing
      // this page can do until the person has one. The return URL stays relative because
      // AccountController only follows a local one, and it carries the code so nobody retypes it.
      if (error?.response?.status === 401) {
        const returnUrl = userCode ? `${window.location.pathname}?userCode=${encodeURIComponent(userCode)}` : window.location.pathname;

        window.location.href = `${BASE_PATH}/account/login?returnUrl=${encodeURIComponent(returnUrl)}`;
        return t('Redirigiendo al inicio de sesión');
      }

      switch (error?.response?.data?.code) {
        case DeviceError.Missing:
          return t('Introduce el código que muestra la terminal.');
        case DeviceError.Unknown:
          return t('El código no es válido o ha caducado. Vuelve a ejecutar j2l auth login.');
        case DeviceError.Refused:
          return t('La solicitud ya no se puede confirmar. Vuelve a ejecutar j2l auth login.');
        default:
          return t('Error inesperado');
      }
    },
    [t, userCode]
  );

  const load = useCallback(
    async (code: string) => {
      if (!code) return;

      setErrorMsg(undefined);
      setIsLoading(true);

      try {
        setAuthorization(await api.get(code));
      } catch (e) {
        setAuthorization(undefined);
        setErrorMsg(describe(e));
      } finally {
        setIsLoading(false);
      }
    },
    [describe]
  );

  useEffect(() => {
    const fromQuery = searchParams.get('userCode');
    if (fromQuery) load(fromQuery);
  }, [searchParams, load]);

  const respond = async (approve: boolean) => {
    setErrorMsg(undefined);
    setIsLoading(true);

    try {
      if (approve) await api.approve(userCode);
      else await api.deny(userCode);

      setOutcome(approve ? 'approved' : 'denied');
    } catch (e) {
      setErrorMsg(describe(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div id="device-container">
      <div id="logo-container">
        <LogoColor />
      </div>
      <div id="form-container">
        <Spin spinning={isLoading} size="large">
          <Card>
            {outcome ? (
              <>
                <Title style={{ fontSize: 24, marginBottom: 22 }} level={4}>
                  {outcome === 'approved' ? t('Acceso autorizado') : t('Acceso denegado')}
                </Title>
                <Paragraph>
                  {outcome === 'approved'
                    ? t('Ya puedes volver a la terminal. Esta ventana se puede cerrar.')
                    : t('No se ha concedido acceso. Esta ventana se puede cerrar.')}
                </Paragraph>
              </>
            ) : (
              <>
                <Title style={{ fontSize: 24, marginBottom: 22 }} level={4}>
                  {t('Autorizar dispositivo')}
                </Title>

                {errorMsg && <Alert type="error" showIcon message={errorMsg} style={{ marginBottom: 16 }} />}

                {authorization ? (
                  <>
                    <Paragraph>
                      {t('Vas a dar acceso a')} <Text strong>{authorization.clientName}</Text> {t('con tu cuenta.')}
                    </Paragraph>

                    <Paragraph>
                      <Text type="secondary">{t('Código')}: </Text>
                      <Text code>{authorization.userCode}</Text>
                    </Paragraph>

                    {authorization.scopes.length > 0 && (
                      <Paragraph>
                        <Text type="secondary">{t('Podrá acceder a')}:</Text>
                        <div className="device-scopes">
                          {authorization.scopes.map((scope) => (
                            <Tag key={scope.name} color={scope.emphasize ? 'blue' : 'default'} title={scope.description ?? scope.name}>
                              {nameScope(scope)}
                            </Tag>
                          ))}
                        </div>
                      </Paragraph>
                    )}

                    <Space>
                      <Button type="primary" disabled={isLoading} onClick={() => respond(true)}>
                        {t('Autorizar')}
                      </Button>
                      <Button disabled={isLoading} onClick={() => respond(false)}>
                        {t('Denegar')}
                      </Button>
                    </Space>
                  </>
                ) : (
                  <Form layout="vertical" onFinish={() => load(userCode)}>
                    <Paragraph>{t('Introduce el código que muestra la terminal.')}</Paragraph>
                    <Form.Item>
                      {/*
                        Numeric because Duende's DeviceFlow.DefaultUserCodeType is Numeric and no client
                        overrides it. inputMode matters more than it looks: the grant exists so somebody
                        can approve from a phone, and that is where a text keyboard costs the most.
                      */}
                      <Input
                        value={userCode}
                        maxLength={32}
                        inputMode="numeric"
                        placeholder="123456789"
                        onChange={(e) => setUserCode(e.target.value.trim())}
                      />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" disabled={isLoading || userCode.length === 0}>
                      {t('Continuar')}
                    </Button>
                  </Form>
                )}
              </>
            )}
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default DeviceView;
